/* eslint-disable max-lines */
import {
  forwardRef, memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, useReducer,
  type Component, type ReactNode
} from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import keys from 'lodash/keys';
import find from 'lodash/find';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import toString from 'lodash/toString';
import toSafeInteger from 'lodash/toSafeInteger';
import { useLazyQuery } from '@apollo/client';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import Box, { type BoxProps } from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
// Material Icon imports
import FormatListChecks from 'mdi-material-ui/FormatListChecks';
// Skillmore UI Components
import { getIds, getStringifiedIds } from '@empathco/ui-components/src/helpers/strings';
import useQueryObject from '@empathco/ui-components/src/hooks/useQueryObject';
import useQueryCounted from '@empathco/ui-components/src/hooks/useQueryCounted';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import FilterSelector from '@empathco/ui-components/src/elements/FilterSelector';
import OnOffSwitch from '@empathco/ui-components/src/elements/OnOffSwitch';
import LevelSelector from '@empathco/ui-components/src/elements/LevelSelector';
import ScopeSelector from '@empathco/ui-components/src/elements/ScopeSelector';
// local imports
import { getLeaderId, getSelectedLeaderId } from '../models/user';
import { EmployeeManagementLevel } from '../constants/managementLevel';
import {
  BestMatchesSort, BEST_MATCHES_SORTS, BEST_MATCHES_SORT_UNDEFINED, isValidSortBy, isValidSortParam
} from '../constants/tbBestMatchesSort';
import { isValidRoleScope, RoleScope, ROLE_SCOPES } from '../constants/scopes';
import { ORGS_QUERY } from '../graphql/Orgs';
import { HIERARCHY_INTERNAL_QUERY } from '../graphql/HierarchyInternal';
import { HIERARCHY_QUERY } from '../graphql/Hierarchy';
import { COUNTRIES_QUERY } from '../graphql/Countries';
import { STATES_QUERY } from '../graphql/States';
import {
  JobCategory, LookupItem, Org, State, Manager,
  OrgsDocument, CountriesDocument, StatesDocument
} from '../graphql/types';
import { JobLookupItem } from '../graphql/customTypes';
import { MAX_COUNTRIES_OPTIONS, MAX_LOOKUP_OPTIONS, MAX_MANAGER_DASHBOARD_ITEMS } from '../config/params';
import useCustomerSettings from '../config/customer';
import { HEIRARCHY_OPTIONS } from '../helpers/graphql';
import useModels, { getDefaultManager, sanitizeLookup } from '../helpers/models';
import { getSettingsBoolValue, getSettingsIntValue, getSettingsStrValue } from '../helpers/context';
import { toggleReducer } from '../helpers/reducers';
import { FilterValues } from '../hooks/useFilters';
import { getLeaderOrg, PersistentContext } from '../context/persistent';
import { GlobalContext } from '../context/global';
import { DataContext } from '../context';
import SortSelector from '../elements/SortSelector';
import ChainOfCommandSelector from '../v3/ChainOfCommandSelector';
import JobSearch, { JobSearchVariant } from '../v3/JobSearch';
import EditJobAdvancedSkills from '../components/EditJobAdvancedSkills';
// SCSS imports
import { orgLabel, orgFilter, employeeRatedFilter, foldingButton, jobSearch } from './DashboardFilters.module.scss';

const NO_ORGS = [] as Org[];
const NO_LOOKUP_ITEMS = [] as LookupItem[];
const NO_MANAGERS = [] as Manager[];
const NO_LEVELS = [] as EmployeeManagementLevel[];
const NO_SKILL_IDS = {} as Record<string, boolean>;

export type OrgFilterPlacement = 'beforeReset' | 'overlap' | 'bottom' | 'simple';

type DashboardFiltersProps = {
  readonly settingsId?: string;
  readonly init?: FilterValues | null;
  readonly action?: ReactNode;
  readonly withScope?: boolean;
  readonly withLevels?: boolean | null;
  readonly withOrg?: boolean | 'disabled' | null;
  readonly requireOrg?: boolean | null;
  readonly withOrgSkills?: boolean | 'collapsed';
  readonly withEmployeeRated?: boolean | null;
  readonly withJobVariant?: JobSearchVariant | null;
  readonly withSort?: boolean | null;
  readonly withReset?: boolean | null;
  readonly compact?: boolean | null;
  readonly uid?: string | null;
  readonly rootUid?: string | null;
  readonly userOrgId?: number | null;
  readonly delegate?: boolean | null;
  readonly withDirectReports?: boolean;
  readonly onChange: (filters: FilterValues) => void;
  readonly onReset?: () => void;
  readonly disabled?: boolean | null;
  readonly disableTopLevel?: boolean | null;
  readonly orgFilterPlacement?: OrgFilterPlacement;
  readonly withoutHierarchy?: boolean | null;
  readonly withoutLocation?: boolean | null;
  readonly extraFilters?: ReactNode | null;
}

const DashboardFilters = forwardRef<Component<BoxProps>, DashboardFiltersProps>(({
  settingsId,
  init,
  action,
  withScope = false,
  withLevels = false,
  withOrg: parentWithOrg,
  requireOrg,
  withOrgSkills: parentWithOrgSkills = false,
  withEmployeeRated = false,
  withJobVariant,
  withSort = false,
  withReset = false,
  compact = false,
  uid,
  rootUid,
  userOrgId,
  delegate = false,
  withDirectReports = false,
  onChange,
  onReset,
  disabled: parentDisabled = false,
  disableTopLevel = false,
  orgFilterPlacement = 'bottom',
  withoutHierarchy = false,
  withoutLocation = false,
  extraFilters
  // eslint-disable-next-line complexity, max-statements, max-lines-per-function
}, ref) => {
  const { formatMessage } = useIntl();
  const { isDomesticCountryId } = useModels();
  const { MANAGEMENT_LEVEL_FIRST, MANAGEMENT_LEVEL_LAST } = useCustomerSettings();

  const withOrg = Boolean(parentWithOrg);
  const orgsDisabled = parentWithOrg === 'disabled';

  const { leaderOrg, updateLeaderOrg } = useContext(PersistentContext);
  const { user: { data: user } } = useContext(GlobalContext);
  const {
    settings: { data: settingsData, pending: pendingSettings, failed: failedSettings },
    settingsUpdate: { pending: pendingSettingsUpdate }, updateSettings
  } = useContext(DataContext);

  // lazy load orgs
  const { query: getOrgs, pending: orgsPending, failed: orgsFailed, results: orgsData } = useQueryCounted({
    data: undefined as unknown as Org,
    key: 'orgs',
    lazyQuery: useLazyQuery(ORGS_QUERY as typeof OrgsDocument)
  });  

  // lazy load company hierarchy
  const {
    query: getHierarchyInternal, pending: pendingHierarchyInt, failed: failedHierarchyInt, results: hierarchyInt
  } = useQueryObject({
    data: undefined,
    key: 'hierarchyInternal',
    flatResults: true,
    lazyQuery: useLazyQuery(HIERARCHY_INTERNAL_QUERY)
  });
  const { query: getHierarchy, pending: pendingHierarchy, failed: failedHierarchy, results: hierarchy } = useQueryObject({
    data: undefined as unknown as Manager[],
    key: 'readHierarchy',
    flatResults: true,
    lazyQuery: useLazyQuery(HIERARCHY_QUERY, HEIRARCHY_OPTIONS)
  });

  // lazy load countires
  const { query: getCountries, pending: pendingCountries, failed: failedCountries, results: countriesData } = useQueryCounted({
    data: undefined as unknown as LookupItem,
    key: 'countries',
    lazyQuery: useLazyQuery(COUNTRIES_QUERY as typeof CountriesDocument)
  });
  // lazy load states
  const { query: getStates, pending: pendingStates, failed: failedStates, results: statesData } = useQueryCounted({
    data: undefined as unknown as State,
    key: 'states',
    lazyQuery: useLazyQuery(STATES_QUERY as typeof StatesDocument)
  });

  const orgs = (((!withOrg && !requireOrg) || orgsFailed) && NO_ORGS) || (orgsPending ? null : orgsData);
  const countries = ((withoutLocation || failedCountries) && NO_LOOKUP_ITEMS) || (pendingCountries ? null : countriesData);
  const states = ((withoutLocation || failedStates) && NO_LOOKUP_ITEMS) || (pendingStates ? null : statesData);
  const managers = ((withoutHierarchy || failedHierarchyInt || failedHierarchy) && NO_MANAGERS) ||
    (!pendingHierarchyInt && !pendingHierarchy && (
      rootUid ? getLeaderOrg(rootUid, leaderOrg) : hierarchy
    )) || null;

  const settingsLoaded = !settingsId || (pendingSettings === false && failedSettings === false && Boolean(settingsData));
  const settings = settingsId && settingsLoaded ? settingsData : null;

  const settingsManager = init
    ? init.manager_id
    : (settingsId && getSettingsStrValue(settings, `${settingsId}__manager`)) || undefined;
  const settingsCountry = init
    ? init.country_id || undefined
    : (settingsId && getSettingsIntValue(settings, `${settingsId}__country`)) || undefined;
  const settingsState = init
    ? init.state_id || undefined
    : (settingsId && getSettingsIntValue(settings, `${settingsId}__state`)) || undefined;
  /* eslint-disable no-nested-ternary */
  const settingsScope = withScope
    ? init
      ? (init.job_category === JobCategory.technology && 'tech') || init.job_category || undefined
      : (settingsId && getSettingsStrValue(settings, `${settingsId}__scope`)) || undefined
    : undefined;
  const settingsLevels: EmployeeManagementLevel[] | undefined = useMemo(() => withLevels
    ? init
      ? init.job_levels || undefined
      : (settingsId &&
          getIds(getSettingsStrValue(settings, `${settingsId}__levels`), MANAGEMENT_LEVEL_FIRST, MANAGEMENT_LEVEL_LAST)
        ) || undefined
    : undefined, [settings, init, settingsId, withLevels, MANAGEMENT_LEVEL_FIRST, MANAGEMENT_LEVEL_LAST]);
  const settingsRated = withEmployeeRated
    ? init
      ? init.employee_rated_only || undefined
      : (settingsId && getSettingsBoolValue(settings, `${settingsId}__rated`)) || undefined
    : undefined;
  /* eslint-enable no-nested-ternary */
  const settingsSort = withSort && settingsId ? getSettingsStrValue(settings, `${settingsId}__sort`) : undefined;

  const [defaultManager, resetManager] = useMemo(() => withoutHierarchy ? [{ id: uid }, { id: uid }] as Manager[] : [
    getDefaultManager(uid, managers, withDirectReports, settingsManager),
    getDefaultManager(uid, managers, withDirectReports)
  ], [uid, settingsManager, managers, withDirectReports, withoutHierarchy]);
  const defaultOrgId = (requireOrg && (userOrgId || orgs?.[0]?.id)) || 0;
  const resetOrgId = (requireOrg !== false && resetManager?.org_id) || defaultOrgId;

  const [manager, setManager] = useState(defaultManager?.id);
  const [org, setOrg] = useState<number>(
    ((withOrg || requireOrg) && (init ? init.org_id : requireOrg !== false && defaultManager?.org_id)) || defaultOrgId);
  const [country, setCountry] = useState<number>(withoutLocation ? 0 : sanitizeLookup(settingsCountry, countries));
  const [state, setState] = useState<number>(withoutLocation ? 0 : sanitizeLookup(settingsState, states));
  const [scope, setScope] = useState<RoleScope | ''>(
    withScope && isValidRoleScope(settingsScope) ? settingsScope as RoleScope : ''
  );
  const [levels, setLevels] = useState<EmployeeManagementLevel[]>(settingsLevels || NO_LEVELS);
  const [rated, setRated] = useState<boolean | undefined>(settingsRated);
  const [job, setJob] = useState<JobLookupItem | null>(null);
  const [sort, setSort] = useState<BestMatchesSort>(
    isValidSortBy(settingsSort) ? settingsSort as BestMatchesSort : BEST_MATCHES_SORT_UNDEFINED);

  const isDomestic = useMemo(() => !withoutLocation && isDomesticCountryId(country, countries),
    [countries, country, isDomesticCountryId, withoutLocation]);

  const withOrgSkills = Boolean(parentWithOrgSkills);
  const orgSkills = useMemo(() => withOrgSkills && orgs && org ? find(orgs, ['id', org])?.skills : undefined,
    [org, orgs, withOrgSkills]);

  const [skillsUnfolded, toggleSkillsUnfolded] = useReducer(toggleReducer, false);
  const skillsPanelDisabled = size(orgSkills) <= MAX_MANAGER_DASHBOARD_ITEMS;
  const skillsPanelOpen = skillsUnfolded && parentWithOrgSkills !== 'collapsed' && !skillsPanelDisabled;

  const [appliedSkillIds, setAppliedSkillIds] = useState(NO_SKILL_IDS);
  const [selectedSkillIds, setSelectedSkillIds] = useState(NO_SKILL_IDS);
  const orgSkillsChanged = useMemo(() => size(appliedSkillIds) >= 1 && !isEqual(appliedSkillIds, NO_SKILL_IDS),
    [appliedSkillIds]);
  const canApplySelectedSkills = useMemo(() => size(selectedSkillIds) >= 1 && !isEqual(selectedSkillIds, appliedSkillIds),
    [selectedSkillIds, appliedSkillIds]);
  useEffect(() => {
    if (withOrgSkills && org) {
      setAppliedSkillIds(NO_SKILL_IDS);
      setSelectedSkillIds(NO_SKILL_IDS);
    }
  }, [org, withOrgSkills]);

  const lookupsLoaded = Boolean(orgs && countries && managers) && !isUndefined(manager);
  const disabled = parentDisabled || pendingSettingsUpdate ? true : undefined;
  const filtersDisabled = disabled || !lookupsLoaded ? true : undefined;

  const handleSelectedSkillsApply = useCallback(() => {
    setAppliedSkillIds(selectedSkillIds);
  }, [selectedSkillIds]);

  const handleSelectedSkillsReset = useCallback(() => {
    setSelectedSkillIds(NO_SKILL_IDS);
    setAppliedSkillIds(NO_SKILL_IDS);
  }, []);

  const handleSelectSkill = useCallback((skillId: number) => {
    const idStr = toString(skillId);
    setSelectedSkillIds((prevIds) => prevIds[idStr] ? omit(prevIds, idStr) : {
      ...prevIds,
      [idStr]: true
    });
  }, []);

  const handleManager = useCallback((value: string, _hasTeams?: boolean, orgId?: number | null) => {
    setManager(value);
    if (withOrg || requireOrg) setOrg(sanitizeLookup(orgId, orgs) || defaultOrgId);
    if (settingsId && settingsManager !== value) updateSettings?.({ [`${settingsId}__manager`]: value });
  }, [orgs, settingsManager, defaultOrgId, withOrg, requireOrg, settingsId, updateSettings]);

  const handleOrg = useCallback((value: number) => setOrg(
    sanitizeLookup(value, orgs) || defaultOrgId
  ), [orgs, defaultOrgId]);

  const handleCountry = useCallback((value: number) => {
    const val = sanitizeLookup(value, countries);
    setCountry(val);
    if (settingsId && settingsCountry !== val) updateSettings?.({ [`${settingsId}__country`]: val });
  }, [countries, settingsCountry, settingsId, updateSettings]);

  const handleState = useCallback((value: number) => {
    const val = sanitizeLookup(value, states);
    setState(val);
    if (settingsId && settingsState !== val) updateSettings?.({ [`${settingsId}__state`]: val });
  }, [states, settingsState, settingsId, updateSettings]);

  const handleScope = useCallback((value: string) => {
    const val = value === scope || !isValidRoleScope(value) ? '' : value as RoleScope;
    setScope(val);
    if (settingsId && settingsScope !== val) updateSettings?.({ [`${settingsId}__scope`]: val });
  }, [scope, settingsId, settingsScope, updateSettings]);

  const handleLevels = useCallback((value: EmployeeManagementLevel[]) => {
    setLevels(value);
    if (settingsId && !isEqual(settingsLevels, value)) updateSettings?.({
      [`${settingsId}__levels`]: getStringifiedIds(value)
    });
  }, [settingsId, settingsLevels, updateSettings]);

  const handleRated = useCallback((value: boolean) => {
    const val = Boolean(value);
    setRated(val);
    if (settingsId && Boolean(settingsRated) !== val) updateSettings?.({ [`${settingsId}__rated`]: val });
  }, [settingsId, settingsRated, updateSettings]);

  const handleSort = useCallback((sortValue: string, _newDir?: boolean | null) => {
    if (isValidSortBy(sortValue)) {
      setSort(sortValue as BestMatchesSort);
      if (settingsId && settingsSort !== sortValue) updateSettings?.({ [`${settingsId}__sort`]: sortValue });
    }
  }, [settingsSort, updateSettings, settingsId]);

  // eslint-disable-next-line complexity
  const handleResetFilters = useCallback(() => {
    setManager(resetManager?.id || '0');
    if (!withoutLocation) {
      setCountry(0);
      setState(0);
    }
    if (withScope) setScope('');
    if (withLevels) setLevels([]);
    if (withEmployeeRated) setRated(false);
    if (withSort) setSort(BEST_MATCHES_SORT_UNDEFINED);
    if (withOrg || requireOrg) {
      setOrg(resetOrgId);
      if (withOrgSkills) {
        setAppliedSkillIds(NO_SKILL_IDS);
        setSelectedSkillIds(NO_SKILL_IDS);
      }
    }
    if (withJobVariant) setJob(null);
    if (settingsId) updateSettings?.({
      [`${settingsId}__manager`]: resetManager?.id || '0',
      ...withoutLocation ? {} : {
        [`${settingsId}__country`]: 0,
        [`${settingsId}__state`]: 0
      },
      ...withScope ? { [`${settingsId}__scope`]: '' } : {},
      ...withLevels ? { [`${settingsId}__levels`]: '' } : {},
      ...withEmployeeRated ? { [`${settingsId}__rated`]: false } : {},
      ...withSort ? { [`${settingsId}__sort`]: BEST_MATCHES_SORT_UNDEFINED } : {}
    });
    onReset?.();
  }, [
    resetOrgId, resetManager,
    withScope, withLevels, withEmployeeRated, withSort, withOrg, requireOrg, withOrgSkills, withJobVariant, withoutLocation,
    onReset, updateSettings, settingsId
  ]);

  useEffect(() => {
    if (!withoutHierarchy) getHierarchyInternal?.();
  }, [withoutHierarchy, getHierarchyInternal]);
  useEffect(() => {
    if (!withoutHierarchy && hierarchyInt) getHierarchy?.();
  }, [withoutHierarchy, hierarchyInt, getHierarchy]);

  useEffect(() => {
    if (rootUid && hierarchy) updateLeaderOrg(hierarchy, rootUid);
  }, [rootUid, hierarchy, updateLeaderOrg]);

  useEffect(() => {
    if (!isUndefined(defaultManager)) {
      setManager((prevManager) => isUndefined(prevManager) ? defaultManager?.id : prevManager);
      if ((withOrg || requireOrg) && requireOrg !== false) setOrg(
        (prevOrg) => prevOrg || defaultManager?.org_id || defaultOrgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOrgId, defaultManager]); // we do not need to track `withOrg` and `requireOrg` changes

  useEffect(() => {
    if (!withoutLocation) getCountries?.({ variables: { limit: MAX_COUNTRIES_OPTIONS } });
  }, [withoutLocation, getCountries]);

  useEffect(() => {
    if (!withoutLocation && isDomestic && country) getStates?.({ variables: {
      country_id: country,
      limit: MAX_COUNTRIES_OPTIONS
    } });
  }, [country, isDomestic, withoutLocation, getStates]);

  useEffect(() => {
    if (withOrg || requireOrg) {
      const selected_leader_id = getSelectedLeaderId(user);
      getOrgs?.({ variables: {
        ...selected_leader_id ? { selected_leader_id } : {},
        limit: MAX_LOOKUP_OPTIONS
      } });
    }
  }, [user, withOrg, requireOrg, getOrgs]);

  // eslint-disable-next-line complexity
  useLayoutEffect(() => {
    if ((init || settingsId) && settingsLoaded && !pendingSettingsUpdate) {
      if (!withoutLocation) {
        setCountry(sanitizeLookup((
          init ? init.country_id : settingsId && getSettingsIntValue(settings, `${settingsId}__country`)
        ) || 0, countries));
        setState(sanitizeLookup((
          init ? init.state_id : settingsId && getSettingsIntValue(settings, `${settingsId}__state`)
        ) || 0, states));
      }
      if (withScope) {
        const scopeFromSettings = (init?.job_category === JobCategory.technology && 'tech') ||
          init?.job_category || (settingsId && getSettingsStrValue(settings, `${settingsId}__scope`)) || '';
        setScope(isValidRoleScope(scopeFromSettings) ? scopeFromSettings as RoleScope : '');
      }
      if (withLevels) setLevels((init
          ? init.job_levels
          : settingsId &&
            getIds(getSettingsStrValue(settings, `${settingsId}__levels`), MANAGEMENT_LEVEL_FIRST, MANAGEMENT_LEVEL_LAST)
        ) || NO_LEVELS);
      if (withEmployeeRated) setRated(Boolean(init
        ? init.employee_rated_only
        : (settingsId && getSettingsBoolValue(settings, `${settingsId}__rated`)) || undefined
      ));
      if (withSort && settingsId) {
        const sortFromSettings = getSettingsStrValue(settings, `${settingsId}__sort`);
        setSort(isValidSortBy(sortFromSettings) ? sortFromSettings as BestMatchesSort : BEST_MATCHES_SORT_UNDEFINED);
      }
    }
  }, [
    settingsLoaded, pendingSettingsUpdate, countries, states, settings, init,
    withScope, withLevels, withEmployeeRated, withSort, withoutLocation, settingsId,
    MANAGEMENT_LEVEL_FIRST, MANAGEMENT_LEVEL_LAST
  ]);

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (settingsLoaded && lookupsLoaded) {
      onChange({
        manager_id: manager === '0' ? managers?.[0]?.topId : manager,
        direct_reports_only: withDirectReports && manager === '0',
        ...withoutLocation ? {} : { country_id: country },
        ...!withoutLocation && isDomestic ? { state_id: state } : {},
        ...withOrg || (requireOrg && org) ? { org_id: org } : {},
        ...withOrg && withOrgSkills && orgSkillsChanged && size(appliedSkillIds) >= 1
          ? { skill_ids: sortBy(map(keys(appliedSkillIds), toSafeInteger)) } : {},
        ...withScope && scope ? { job_category: scope === 'tech' ? JobCategory.technology : scope as JobCategory } : {},
        ...withLevels ? { job_levels: levels } : {},
        ...withEmployeeRated ? { employee_rated_only: rated } : {},
        ...withJobVariant && job?.id ? { job_id: job.id } : {},
        ...withSort && isValidSortParam(sort) ? { sort_by: sort } : {}
      });
    }
  }, [
    country, state, manager, org, scope, levels, rated, job, sort, appliedSkillIds, orgSkillsChanged, onChange,
    settingsLoaded, lookupsLoaded, isDomestic, managers,
    withScope, withLevels, withEmployeeRated, withSort, withOrg, withOrgSkills, requireOrg,
    withJobVariant, withDirectReports, withoutLocation
  ]);

  const orgInput = withOrg && (!orgs || size(orgs) >= 1) ? (
    <Box
        pl={orgFilterPlacement === 'beforeReset' ? 2 : undefined}
        pr={1.25}
        pb={1}
        display="flex"
        alignItems="center"
        flexWrap="wrap"
    >
      {orgFilterPlacement === 'simple' ? undefined : (
        <BoxTypography pr={1.5} variant="caption" color="info.caption" className={orgLabel}>
          <FormattedMessage id="hr.dashboard.org"/>
        </BoxTypography>
      )}
      <FilterSelector
          type="skills_org"
          choices={orgs}
          value={org}
          onChange={handleOrg}
          required={requireOrg}
          disabled={filtersDisabled || orgsDisabled}
      />
      {withOrgSkills ? (
        <Button
            disableElevation
            onClick={toggleSkillsUnfolded}
            variant={skillsPanelOpen || orgSkillsChanged ? 'contained' : 'text'}
            color="primary"
            aria-label={formatMessage({ id: 'hr.dashboard.button.refine' })}
            disabled={filtersDisabled || orgsDisabled || skillsPanelDisabled}
            className={foldingButton}
        >
          <FormatListChecks/>
        </Button>
      ) : undefined}
    </Box>
  ) : undefined;

  const orgBox = orgInput ? (orgFilterPlacement === 'simple' && orgInput) || (
    <Box
        flexGrow={orgFilterPlacement === 'beforeReset' ? undefined : 1}
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        pt={orgFilterPlacement === 'beforeReset' ? undefined : 1}
        className={orgFilterPlacement === 'overlap' ? orgFilter : undefined}
    >
      {orgInput}
    </Box>
  ) : undefined;

  return (
    <>
      <Box
          ref={ref}
          flexGrow={1}
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="flex-start"
          pt={compact ? 1 : undefined}
      >
        {withScope ? (
          <Box pr={1.25} pb={1} display="flex">
            <ScopeSelector
                simple
                scope={ROLE_SCOPES}
                value={scope}
                onChange={handleScope}
                disabled={disabled ? true : undefined}
            />
          </Box>
        ) : undefined}
        {withoutHierarchy || (managers && size(managers) < 1) ? undefined : (
          <Box pr={1.25} pb={1} display="flex" justifyContent="flex-end">
            <ChainOfCommandSelector
                uid={defaultManager?.id}
                me={resetManager?.id}
                leader={getLeaderId(user)}
                delegate={delegate}
                disableTopLevel={disableTopLevel}
                expandable
                allowEmpty={withDirectReports}
                managers={managers}
                value={manager || ''}
                onChange={handleManager}
                disabled={filtersDisabled}
                placement="left"
            />
          </Box>
        )}
        {withoutLocation ? undefined : (
          <Box pr={1.25} pb={1} display="flex" justifyContent="flex-end">
            <FilterSelector
                type="country"
                choices={countries}
                value={country}
                onChange={handleCountry}
                disabled={filtersDisabled}
            />
          </Box>
        )}
        {withoutLocation ? undefined : (
          <Box pr={1.25} pb={1} display="flex" justifyContent="flex-end">
            <FilterSelector
                type="state"
                choices={isDomestic ? states : undefined}
                value={isDomestic ? state : 0}
                onChange={handleState}
                disabled={filtersDisabled || !isDomestic}
            />
          </Box>
        )}
        {withLevels ? (
          <Box pr={1.25} pb={1} display="flex" justifyContent="flex-end">
            <LevelSelector
                variant="job"
                multiple
                minValue={MANAGEMENT_LEVEL_FIRST}
                maxValue={MANAGEMENT_LEVEL_LAST}
                values={levels}
                onChangeMulpiple={handleLevels}
                disabled={filtersDisabled ? true : undefined}
            />
          </Box>
        ) : undefined}
        {withSort ? (
          <Box pr={1.25} pb={1} display="flex" justifyContent="flex-end">
            <SortSelector
                variant="best_matches"
                value={sort}
                onChange={handleSort}
                disabled={filtersDisabled ? true : undefined}
                values={BEST_MATCHES_SORTS}
                defaultValue={BEST_MATCHES_SORT_UNDEFINED}
            />
          </Box>
        ) : undefined}
        {withJobVariant ? (
          <Box flexGrow={1} pr={1.25} pb={1} display="flex" justifyContent="flex-end">
            <JobSearch
                variant={withJobVariant}
                withSkills
                fullWidth
                value={job}
                onChange={setJob}
                disabled={filtersDisabled}
                className={jobSearch}
            />
          </Box>
        ) : undefined}
        {orgFilterPlacement === 'simple' ? orgBox : undefined}
        {extraFilters}
        {orgFilterPlacement === 'beforeReset' ? orgBox : undefined}
        {withReset ? (
          <Box pb={1}>
            <Button
                color="primary"
                variant="text"
                disabled={disabled || pendingHierarchyInt || pendingHierarchy ||
                  (!withoutLocation && (pendingCountries || (isDomestic && pendingStates))) ||
                  (country === 0 && state === 0 && size(levels) === 0 && manager === resetManager?.id &&
                  sort === BEST_MATCHES_SORT_UNDEFINED && (!withJobVariant || isNil(job)) && (!withScope || !scope) &&
                  (!withOrg || org === resetOrgId) && !orgSkillsChanged && !onReset)}
                onClick={handleResetFilters}
            >
              <FormattedMessage id="common.button.reset"/>
            </Button>
          </Box>
        ) : undefined}
        {withEmployeeRated ? (
          <Box pl={1} pb={1} flex="1 1 0" display="flex" justifyContent="flex-end" className={employeeRatedFilter}>
            <OnOffSwitch
                label="hr.dashboard.employee_rated"
                value={Boolean(rated)}
                onChange={handleRated}
                disabled={filtersDisabled ? true : undefined}
            />
          </Box>
        ) : undefined}
        {action ? (
          <Box pl={1} pb={1} flex="1 1 0" display="flex" justifyContent="flex-end">
            {action}
          </Box>
        ) : undefined}
      </Box>
      {orgFilterPlacement === 'beforeReset' || orgFilterPlacement === 'simple' ? undefined : orgBox}
      {withOrgSkills && orgSkills && selectedSkillIds ? (
        <Collapse in={skillsPanelOpen}>
          <EditJobAdvancedSkills
              variant="analytics"
              skills={orgSkills}
              advancedIds={selectedSkillIds}
              disabled={filtersDisabled}
              disableUnselected={size(selectedSkillIds) >= MAX_MANAGER_DASHBOARD_ITEMS}
              onActivate={handleSelectSkill}
              onApply={canApplySelectedSkills ? handleSelectedSkillsApply : undefined}
              onReset={orgSkillsChanged ? handleSelectedSkillsReset : undefined}
          />
        </Collapse>
      ) : undefined}
    </>
  );
});

DashboardFilters.displayName = 'DashboardFilters';

export default memo(DashboardFilters);
